/**
 * socialAi.ts — AI-written social post drafts for ONE approved article and ONE network.
 *
 * The AI returns SUGGESTIONS only. Nothing is saved or sent: a person picks one, edits it, and it then goes through the
 * normal social-post steps (approval by a different person). Rules enforced by code, in the AI Worker and
 * again in the CMS Worker (never trusted from the model):
 *   • the network's length limit (minus room for the article link, which the CMS adds, never the model);
 *   • no web addresses in the text, no HTML;
 *   • at most 3 hashtags;
 *   • every figure must already appear in the article, the person's note, or the brand's own profile / product facts.
 * Network limits come from social.ts (as published by the networks).
 */
import { z } from "zod";
import { aiModelChoiceSchema, aiProviderSchema, memoryPackSchema, brainTexts } from "./ai.ts";
import { brandProfileSchema } from "./brain.ts";
import { allowedNumbers, findUnsupportedNumbers, type UnsupportedNumber } from "./evidence.ts";
import { socialLength, socialTextLimit } from "./social.ts";

export const SOCIAL_NETWORKS = ["linkedin", "x", "instagram", "threads", "bluesky", "mastodon", "facebook"] as const;
export const socialNetworkSchema = z.enum(SOCIAL_NETWORKS);
export type SocialNetwork = z.infer<typeof socialNetworkSchema>;

export const SOCIAL_AI_LIMITS = {
  maxVariants: 3,
  maxHashtags: 3,
  /** How much of the article the AI reads. */
  maxArticleChars: 6000,
  maxInstructionChars: 300,
} as const;

const articleSchema = z.object({
  title: z.string().min(1).max(300),
  description: z.string().max(600).default(""),
  /** Plain text of the article, cut to maxArticleChars by the CMS. */
  bodyText: z.string().max(SOCIAL_AI_LIMITS.maxArticleChars),
});
export type SocialArticle = z.infer<typeof articleSchema>;

/** CMS Worker -> AI Worker. */
export const socialDraftRequestSchema = z.object({
  model: aiModelChoiceSchema,
  network: socialNetworkSchema,
  article: articleSchema,
  /** Characters kept free for the article link the CMS adds after the text (0 = no link). */
  reserveChars: z.number().int().min(0).max(500).default(0),
  instruction: z.string().trim().max(SOCIAL_AI_LIMITS.maxInstructionChars).optional(),
  count: z.number().int().min(1).max(SOCIAL_AI_LIMITS.maxVariants).default(SOCIAL_AI_LIMITS.maxVariants),
  /** "article": write from an approved article. "idea": there is no article, only a topic; write a point of view and state no facts. */
  mode: z.enum(["article", "idea"]).default("article"),
  /** What the person's own approvals and declines showed (rules a manager left on), one plain sentence each. */
  learned: z.array(z.string().trim().min(1).max(200)).max(10).default([]),
  memory: memoryPackSchema.default({ rules: [], examples: [], preferredTopics: [], avoidedTopics: [], profile: brandProfileSchema.parse({ voice: "", audience: "", avoid: "" }), knowledge: [] }),
});
export type SocialDraftRequest = z.infer<typeof socialDraftRequestSchema>;

/** Browser -> CMS Worker. */
export const socialDraftBodySchema = z.object({
  siteId: z.string().min(1),
  contentId: z.string().min(1),
  network: socialNetworkSchema,
  instruction: z.string().trim().max(SOCIAL_AI_LIMITS.maxInstructionChars).optional(),
  count: z.number().int().min(1).max(SOCIAL_AI_LIMITS.maxVariants).default(SOCIAL_AI_LIMITS.maxVariants),
  includeLink: z.boolean().default(true),
});
export type SocialDraftBody = z.infer<typeof socialDraftBodySchema>;

/** Browser -> CMS Worker: posts for an idea (a topic), with no article behind it. */
export const socialIdeaDraftBodySchema = z.object({
  siteId: z.string().min(1),
  network: socialNetworkSchema,
  /** The idea, in a few words (a topic or a question). */
  title: z.string().trim().min(3).max(160),
  instruction: z.string().trim().max(SOCIAL_AI_LIMITS.maxInstructionChars).optional(),
  count: z.number().int().min(1).max(SOCIAL_AI_LIMITS.maxVariants).default(SOCIAL_AI_LIMITS.maxVariants),
});
export type SocialIdeaDraftBody = z.infer<typeof socialIdeaDraftBodySchema>;

export const socialIdeaDraftResultSchema = z.object({
  network: socialNetworkSchema,
  maxChars: z.number().int().nullable(),
  posts: z.array(z.object({ text: z.string(), angle: z.string().max(160).default("") })),
  dropped: z.number().int().min(0),
  usage: z.object({ provider: aiProviderSchema, model: z.string(), inputTokens: z.number().int(), outputTokens: z.number().int(), costUsd: z.number().nullable() }),
});
export type SocialIdeaDraftResult = z.infer<typeof socialIdeaDraftResultSchema>;

export const socialSuggestionSchema = z.object({
  text: z.string(),
  /** One short line: what this version emphasises. */
  angle: z.string().max(160).default(""),
});

/** AI Worker -> CMS Worker. */
export const socialDraftResponseSchema = z.object({
  posts: z.array(socialSuggestionSchema).max(SOCIAL_AI_LIMITS.maxVariants),
  /** Versions the code threw away (too long, a web address, a figure not in the article ...). */
  dropped: z.number().int().min(0).default(0),
  usage: z.object({ provider: aiProviderSchema, model: z.string(), inputTokens: z.number().int(), outputTokens: z.number().int() }),
});
export type SocialDraftResponse = z.infer<typeof socialDraftResponseSchema>;

/** CMS Worker -> browser. */
export const socialDraftResultSchema = z.object({
  contentId: z.string(),
  network: socialNetworkSchema,
  /** The public address of the article, to add after the text, or null. */
  link: z.string().nullable(),
  /** Characters allowed for the text itself (network limit minus the link). */
  maxChars: z.number().int().nullable(),
  posts: z.array(socialSuggestionSchema),
  dropped: z.number().int(),
  /** Set when the article is not published yet: the link will not work until it is. */
  articleStatus: z.string(),
  usage: z.object({ provider: aiProviderSchema, model: z.string(), inputTokens: z.number().int(), outputTokens: z.number().int(), costUsd: z.number().nullable() }),
});
export type SocialDraftResult = z.infer<typeof socialDraftResultSchema>;

/** Every figure a post may use: the article, the person's note, and what a manager wrote into the brain. */
export function socialAllowedNumbers(req: Pick<SocialDraftRequest, "article" | "instruction" | "memory">): number[] {
  return allowedNumbers({ texts: [req.article.title, req.article.description, req.article.bodyText, req.instruction ?? "", ...brainTexts(req.memory)] });
}

const URL_LIKE = /(?:https?:\/\/|www\.)\S+|\b[a-z0-9-]+\.(?:com|org|net|io|co|ai|app|dev|ly|me|us|uk)\b\/?\S*/i;
const HTML_TAG = /<\/?[a-z][^>]*>/i;
const HASHTAG = /(?:^|\s)#[\p{L}\p{N}_]+/gu;

export interface SocialPostCheck {
  text: string;
  issues: string[];
  unsupported: UnsupportedNumber[];
}

/** The deterministic rulebook for one suggested post. `allowed` = socialAllowedNumbers(...). */
export function checkSocialPost(rawText: string, opts: { network: SocialNetwork; reserveChars: number; allowed: readonly number[] }): SocialPostCheck {
  const issues: string[] = [];
  let text = rawText.replace(/\r\n?/g, "\n").trim();
  const fenced = /^```[a-z]*\n([\s\S]*?)\n```$/i.exec(text);
  if (fenced) text = (fenced[1] ?? "").trim();
  const quoted = /^(["“])([\s\S]*)(["”])$/.exec(text);
  if (quoted && !(quoted[2] ?? "").includes('"')) text = (quoted[2] ?? "").trim();
  text = text.replace(/\n{3,}/g, "\n\n");

  if (text === "") issues.push("The post is empty.");
  const limit = socialTextLimit(opts.network);
  const max = limit ? limit.max - opts.reserveChars : null;
  if (max !== null && socialLength(text) > max) issues.push(`The post is ${socialLength(text)} characters; ${limit!.label} allows ${max} here${opts.reserveChars > 0 ? " (room is kept for the article link)" : ""}.`);
  if (HTML_TAG.test(text)) issues.push("Remove HTML tags.");
  if (URL_LIKE.test(text)) issues.push("Do not write web addresses; the link is added separately.");
  const tags = text.match(HASHTAG)?.length ?? 0;
  if (tags > SOCIAL_AI_LIMITS.maxHashtags) issues.push(`Use at most ${SOCIAL_AI_LIMITS.maxHashtags} hashtags (found ${tags}).`);
  const unsupported = findUnsupportedNumbers(text, opts.allowed);
  if (unsupported.length > 0) issues.push(`These figures are not in the article or the brand's own facts: ${unsupported.slice(0, 3).map((u) => u.raw).join(", ")}.`);
  return { text, issues, unsupported };
}

/** Longest text allowed for the post itself, or null when the network has no known limit. */
export function socialMaxChars(network: SocialNetwork, reserveChars: number): number | null {
  const limit = socialTextLimit(network);
  return limit ? limit.max - reserveChars : null;
}

/** Which network a channel is on, from its service name; null for services the AI does not write for. */
export function socialNetworkOf(service: string): SocialNetwork | null {
  const s = service.trim().toLowerCase();
  if (s === "twitter" || s === "x") return "x";
  return (SOCIAL_NETWORKS as readonly string[]).includes(s) ? (s as SocialNetwork) : s.startsWith("linkedin") ? "linkedin" : s.startsWith("facebook") ? "facebook" : s.startsWith("instagram") ? "instagram" : null;
}

const MULTIPLIER = /\b\d+(?:\.\d+)?\s?(?:x\b|×|times\b)/i;

/**
 * Extra rule for posts written from an idea (no article): a claim like "3x faster" or "10 times better" is a figure even
 * though small numbers are normally allowed ("5 tips"). Returns a plain reason, or null.
 */
export function ideaPostProblem(text: string): string | null {
  return MULTIPLIER.test(text) ? "It claims a multiple (like “3x faster”). A post from an idea may not state figures." : null;
}
